
import { Button } from '@/components/ui/button';
import { 
  LayoutGrid, 
  List, 
  Settings, 
  Bell,
  Search,
  Plus
} from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useState } from 'react';
import { Input } from '@/components/ui/input';

interface DashboardHeaderProps {
  user: any;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
}

export const DashboardHeader = ({ user, viewMode, onViewModeChange }: DashboardHeaderProps) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useScreenSize();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    console.log('📊 [DASHBOARD HEADER] Sign out initiated');
    setSigningOut(true);
    
    try {
      const { error } = await signOut();
      
      if (error) {
        console.error('📊 [DASHBOARD HEADER] Sign out error:', error);
      } else {
        console.log('📊 [DASHBOARD HEADER] Sign out successful, navigating to auth');
        // Force navigation after sign out
        window.location.href = '/auth';
      }
    } catch (error) {
      console.error('📊 [DASHBOARD HEADER] Unexpected sign out error:', error);
    }
    
    setSigningOut(false);
  };

  if (showSearch && isMobile) {
    return (
      <div className="bg-background border-b border-border p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search boards and cards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowSearch(false);
              setSearchQuery('');
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <header className="bg-background border-b border-border shadow-sm">
      <div className={`flex items-center justify-between ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
        {/* Left: Logo & Title */}
        <div className="flex items-center gap-4">
          <h1 className={`font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent ${
            isMobile ? 'text-xl' : 'text-2xl'
          }`}>
            KanbanFlow
          </h1>
          {!isMobile && (
            <span className="text-muted-foreground">Dashboard</span>
          )}
        </div>

        {/* Center: Search (Desktop) */}
        {!isMobile && !isTablet && (
          <div className="flex-1 max-w-md mx-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search boards and cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Search (Mobile/Tablet) */}
          {(isMobile || isTablet) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSearch(true)}
              className="min-h-touch-target-sm min-w-touch-target-sm"
            >
              <Search className="h-4 w-4" />
            </Button>
          )}

          {/* View Mode Toggle */}
          {!isMobile && (
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('grid')}
                className="border-0 rounded-r-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('list')}
                className="border-0 rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Notifications */}
          <Button variant="ghost" size="sm" className="min-h-touch-target-sm min-w-touch-target-sm">
            <Bell className="h-4 w-4" />
          </Button>

          {/* Settings */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/board-settings')}
            className="min-h-touch-target-sm min-w-touch-target-sm"
          >
            <Settings className="h-4 w-4" />
          </Button>

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Info & Sign Out */}
          {!isMobile && (
            <div className="flex items-center gap-2 ml-2">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSignOut}
                disabled={signingOut}
              >
                {signingOut ? 'Signing Out...' : 'Sign Out'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
